// Rules for sending the offline queue. Kept free of browser APIs so they can
// be unit-tested: the store and the send function are passed in.
import { i18n } from '@/i18n';
import type { OutboxItem, TranscriptItem, XrayItem } from '@/lib/outbox';
import type { NewEntry } from '@/types/api';

/** The API accepts at most this many entries per request (docs/API.md). */
export const MAX_BATCH = 50;

export function chunk<T>(items: readonly T[], size = MAX_BATCH): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * "retry": temporary — keep the entry queued and try later (offline, server
 * error, rate limit, expired login). "reject": the server refused this entry
 * itself (validation, conflict, gone) — retrying would not help.
 */
export function failureKind(err: unknown): 'retry' | 'reject' {
  const status =
    typeof err === 'object' && err !== null && 'status' in err
      ? (err as { status: unknown }).status
      : undefined;
  if (typeof status !== 'number') return 'retry';
  if (status === 401 || status === 408 || status === 429 || status >= 500) return 'retry';
  return 'reject';
}

export type OutboxStore = {
  all(): Promise<OutboxItem[]>;
  remove(clientIds: string[]): Promise<void>;
  markFailed(clientIds: string[], reason: string): Promise<void>;
};

export type SendEntries = (
  patientId: string,
  entries: NewEntry[],
  dentistId: string | null,
  visitId: string | null
) => Promise<unknown>;

export type SyncResult = { sent: number; rejected: number; stopped: boolean };

function reason(err: unknown): string {
  return err instanceof Error ? err.message : i18n.t('errors.REJECTED');
}

/** One request per patient, doctor and target visit: the batch carries them once. */
function groupForSending(items: OutboxItem[]): OutboxItem[][] {
  const map = new Map<string, OutboxItem[]>();
  for (const item of items) {
    const key = [item.patientId, item.dentistId ?? '', item.visitId ?? ''].join('|');
    map.set(key, [...(map.get(key) ?? []), item]);
  }
  return [...map.values()];
}

async function sendBatch(batch: OutboxItem[], send: SendEntries, store: OutboxStore) {
  await send(
    batch[0].patientId,
    batch.map((b) => b.entry),
    batch[0].dentistId ?? null,
    batch[0].visitId ?? null
  );
  await store.remove(batch.map((b) => b.clientId));
}

/** A refused batch is retried one entry at a time, so one bad entry does not sink the rest. */
async function sendOneByOne(
  batch: OutboxItem[],
  send: SendEntries,
  store: OutboxStore,
  result: SyncResult
) {
  for (const item of batch) {
    try {
      await sendBatch([item], send, store);
      result.sent += 1;
    } catch (err) {
      if (failureKind(err) === 'retry') {
        result.stopped = true;
        return;
      }
      await store.markFailed([item.clientId], reason(err));
      result.rejected += 1;
    }
  }
}

/**
 * Send the queued entries of one user. Other users' entries on a shared
 * device stay queued until they sign in again.
 */
export async function syncOutbox(
  store: OutboxStore,
  send: SendEntries,
  userId: string
): Promise<SyncResult> {
  const result: SyncResult = { sent: 0, rejected: 0, stopped: false };
  const items = (await store.all()).filter((i) => !i.failed && i.authorId === userId);
  for (const group of groupForSending(items)) {
    for (const batch of chunk(group)) {
      try {
        await sendBatch(batch, send, store);
        result.sent += batch.length;
      } catch (err) {
        if (failureKind(err) === 'retry') {
          result.stopped = true;
          return result;
        }
        await sendOneByOne(batch, send, store, result);
        if (result.stopped) return result;
      }
    }
  }
  return result;
}

export type TranscriptStore = {
  all(): Promise<TranscriptItem[]>;
  sendLatest<T>(clientId: string, send: (item: TranscriptItem) => Promise<T>): Promise<T | null>;
  markFailed(clientId: string, reason: string): Promise<void>;
};

export type SendTranscript = (item: TranscriptItem) => Promise<unknown>;

/**
 * Send the queued dictations of one user, before their entries. Each holds the
 * latest text of one dictation, so one request per dictation is enough.
 */
export async function syncTranscripts(
  store: TranscriptStore,
  send: SendTranscript,
  userId: string
): Promise<SyncResult> {
  const result: SyncResult = { sent: 0, rejected: 0, stopped: false };
  const items = (await store.all()).filter((i) => !i.failed && i.authorId === userId);
  for (const item of items) {
    try {
      if ((await store.sendLatest(item.clientId, send)) !== null) result.sent += 1;
    } catch (err) {
      if (failureKind(err) === 'retry') {
        result.stopped = true;
        return result;
      }
      await store.markFailed(item.clientId, reason(err));
      result.rejected += 1;
    }
  }
  return result;
}

export type XrayStore = {
  all(): Promise<XrayItem[]>;
  remove(clientIds: string[]): Promise<void>;
  markFailed(clientId: string, reason: string): Promise<void>;
};

export type SendXray = (item: XrayItem) => Promise<unknown>;

/**
 * Send the queued X-ray images of one user, one request each (the upload is
 * idempotent by clientId). A network problem stops the run; an image the
 * server refuses is kept, marked, so it is not lost silently.
 */
export async function syncXrays(
  store: XrayStore,
  send: SendXray,
  userId: string
): Promise<SyncResult> {
  const result: SyncResult = { sent: 0, rejected: 0, stopped: false };
  const items = (await store.all()).filter((i) => !i.failed && i.authorId === userId);
  for (const item of items) {
    try {
      await send(item);
      await store.remove([item.clientId]);
      result.sent += 1;
    } catch (err) {
      if (failureKind(err) === 'retry') {
        result.stopped = true;
        return result;
      }
      await store.markFailed(item.clientId, reason(err));
      result.rejected += 1;
    }
  }
  return result;
}
